import React, { useState, useEffect } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { PencilSquareIcon, ArchiveBoxIcon, EyeIcon } from '@heroicons/react/24/outline';
import Button from '../ui/Button';
import { formatDateTime, formatDateTimeLocal } from '../../utils/dateUtils';
import { truncateText } from '../../utils/stringUtils';
import { TASK_CATEGORIES } from '../../constants/status';

// Отдельный компонент для кнопок действий
const ActionButtons = ({ item, openEditModal, handleArchiveTask, openDetailsModal, isCompleted }) => {
  return (
    <div className="flex space-x-1 pointer-events-auto" data-no-dnd="true">
      <Button
        variant="outline"
        size="small"
        onClick={() => {
          if (typeof openDetailsModal === 'function') {
            openDetailsModal(item);
          }
        }}
        className="p-1 min-w-0 text-blue-600 hover:text-blue-800"
        style={{ touchAction: 'manipulation' }}
        title="Просмотреть детали"
      >
        <EyeIcon className="w-4 h-4" />
      </Button>
      <Button
        variant="outline"
        size="small"
        onClick={() => {
          if (typeof openEditModal === 'function') {
            openEditModal(item);
          }
        }}
        className="p-1 min-w-0"
        style={{ touchAction: 'manipulation' }}
        title="Редактировать"
      >
        <PencilSquareIcon className="w-4 h-4" />
      </Button>
      {isCompleted && (
        <Button
          variant="outline"
          size="small"
          onClick={() => {
            if (typeof handleArchiveTask === 'function') {
              handleArchiveTask(item.id);
            }
          }}
          className="p-1 min-w-0 text-yellow-600 hover:text-yellow-800"
          style={{ touchAction: 'manipulation' }}
          title="Архивировать"
        >
          <ArchiveBoxIcon className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
};

const TaskCard = ({
  item,
  index,
  developers,
  projects,
  handleDeveloperChange,
  handleProjectChange,
  handleHoursChange,
  handleArchiveTask,
  openEditModal,
  openDetailsModal,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id || `temp-${index}` });

  // Skip rendering if item has no valid ID
  if (!item || !item.id || item.id === 'undefined' || item.id === undefined) {
    console.warn('TaskCard: Skipping task with invalid ID:', item);
    return null;
  }

  // Логирование для отладки
  console.log(`TaskCard ${item.id}: isDragging = ${isDragging}`);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 9999 : "auto",
  };

  const getDeveloperName = (developerId) => {
    // Сначала ищем разработчика по ID
    const developer = developers.find((dev) => dev.id === developerId);
    if (developer) {
      return developer.name;
    }
    
    // Если не найден по ID, ищем по названию (для случаев когда developerId это имя)
    const developerByName = developers.find((dev) => dev.name === developerId);
    if (developerByName) {
      return developerByName.name;
    }
    
    // Если разработчик не найден, возвращаем ID как есть
    return developerId || "Назначить разработчика";
  };

  const getProjectName = (projectId) => {
    // Сначала ищем проект по ID
    const project = projects.find((proj) => proj.id === projectId);
    if (project) {
      return project.name;
    }
    
    // Если не найден по ID, ищем по названию (для случаев когда projectId это название)
    const projectByName = projects.find((proj) => proj.name === projectId);
    if (projectByName) {
      return projectByName.name;
    }
    
    // Если проект не найден, возвращаем название как есть
    return projectId || "Без проекта";
  };

  const isCompleted = item.category === TASK_CATEGORIES.ITEMS3;

  return (
    <div
      ref={setNodeRef}
      className={`w-full bg-white rounded-lg shadow-md p-4 mb-2 hover:shadow-lg transition-all duration-200 select-none h-48 ${
        isDragging ? 'ring-2 ring-blue-500 ring-opacity-50' : ''
      }`}
      style={style}
      {...attributes}
      {...listeners}
    >
      <div className="flex flex-col w-full h-full">
        {/* Header */}
        <div className="flex justify-between items-start mb-2 w-full flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-800 flex-1 pr-2 select-none line-clamp-1">
            {item.title}
          </h3>
          <ActionButtons 
            item={item} 
            openEditModal={openEditModal} 
            handleArchiveTask={handleArchiveTask} 
            openDetailsModal={openDetailsModal}
            isCompleted={isCompleted} 
          />
        </div>

        {/* Description with fade effect */}
        <div className="mb-3 w-full flex-1 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white opacity-0 pointer-events-none transition-opacity duration-200" 
               style={{ 
                 opacity: item.description.length > 100 ? 1 : 0 
               }} />
          <p className="text-gray-600 text-sm leading-relaxed select-none line-clamp-4">
            {item.description}
          </p>
        </div>

        {/* Task Info - Fixed at bottom */}
        <div className="flex flex-col gap-2 w-full flex-shrink-0">
          {/* Hours */}
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-gray-700 select-none">
              Часы: {item.hours}
            </span>
          </div>

          {/* Developer and Project */}
          <div className="flex items-center justify-between text-sm text-gray-500">
            <div className="flex items-center space-x-2">
              <span className="text-gray-400 select-none">
                {truncateText(getDeveloperName(item.developer), 15)}
              </span>
              <span className="text-gray-300 select-none">•</span>
              <span className="text-gray-400 select-none">
                {truncateText(getProjectName(item.project), 15)}
              </span>
            </div>
          </div>

          {/* Completion Date - Always takes space */}
          <div className="text-xs text-gray-400 border-t border-gray-100 pt-2 select-none min-h-[20px]">
            {isCompleted && item.completedAt ? (
              `Завершено: ${formatDateTimeLocal(item.completedAt)}`
            ) : (
              <span className="invisible">Завершено: </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskCard; 